import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Pencil, Trash2, Loader2, Mail, Phone, MapPin, Euro, CreditCard,
  ShieldCheck, ChevronLeft, ChevronRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from "../components/ui/alert-dialog";
import { EmployeeFormDialog, CONTRACT_LABELS } from "../components/employees/EmployeeFormDialog";
import { FileSection } from "../components/employees/FileSection";
import { WeekGrid } from "../components/employees/WeekGrid";
import { ScheduleEditorDialog } from "../components/employees/ScheduleEditorDialog";
import { EventDialog } from "../components/employees/EventDialog";
import { PlanningNavigation } from "../components/employees/PlanningNavigation";
import { useAuth } from "../context/AuthContext";
import { getEmployee, deleteEmployee, getPlanningWeek } from "../services/api";

const fmtDate = (iso) => iso ? new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "—";
const isoDay = (d) => d.toLocaleDateString("fr-CA");
const mondayOf = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

const PlanningTab = ({ employeeId }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = user?.role === "admin" || !!user?.can_manage_events;
  const [refDate, setRefDate] = useState(new Date());
  const [week, setWeek] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editEmployee, setEditEmployee] = useState(null);
  const [eventDialog, setEventDialog] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await getPlanningWeek(isoDay(mondayOf(refDate)));
      setWeek(res.data);
    } catch { toast.error("Erreur de chargement du planning"); }
    finally { setLoading(false); }
  }, [refDate]);

  useEffect(() => { load(); }, [load]);

  const gridEmployees = (week?.employees || []).filter((e) => e.id === employeeId);
  const gridEvents = (week?.events || []).filter((ev) => (ev.employee_ids || []).length === 0 || ev.employee_ids.includes(employeeId));

  return (
    <div data-testid="planning-tab">
      <PlanningNavigation selectedDate={refDate} onDateChange={setRefDate} title={`Semaine du ${week ? new Date(week.week_start + "T00:00:00").toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : ""}`} onPrevious={() => setRefDate(addDays(refDate, -7))} onNext={() => setRefDate(addDays(refDate, 7))} onToday={() => setRefDate(new Date())} previousId="planning-prev-week" nextId="planning-next-week" titleId="employee-week-title" todayId="employee-week-today" />
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-slate-300" /></div>
      ) : gridEmployees.length > 0 ? (
        <WeekGrid
          focusDate={isoDay(refDate)}
          days={week?.days || []}
          employees={gridEmployees}
          allEmployees={week?.employees || []}
          events={gridEvents}
          tasks={(week?.tasks || []).filter((t) => t.assign_all || (t.employee_ids || []).includes(employeeId))}
          canManage={canManage}
          onEventClick={(ev) => setEventDialog({ event: ev })}
          onEmployeeClick={canManage ? (emp) => setEditEmployee(emp) : undefined}
          onTaskClick={() => navigate("/taches")}
          onChanged={load}
        />
      ) : (
        <div className="text-center py-12 bg-white border border-dashed border-slate-200 rounded-xl">
          <p className="text-slate-400 text-sm">Cet employé n'apparaît pas dans le planning de cette semaine (inactif ou non planifié).</p>
        </div>
      )}
      <ScheduleEditorDialog
        open={!!editEmployee}
        onClose={() => setEditEmployee(null)}
        employee={editEmployee}
        schedule={editEmployee?.template_days}
        schoolWeeks={editEmployee?.school_weeks}
        onSaved={load}
      />
      <EventDialog open={!!eventDialog} onClose={() => setEventDialog(null)} event={eventDialog?.event} onSaved={load} />
    </div>
  );
};

export default function EmployeeDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const canViewPayslips = user?.role === "admin" || user?.employee_id === id;
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getEmployee(id);
      setEmployee(res.data);
    } catch {
      toast.error("Employé non trouvé");
      navigate("/employes");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    try {
      await deleteEmployee(id);
      toast.success("Employé supprimé");
      navigate("/employes");
    } catch { toast.error("Erreur"); }
  };

  if (loading || !employee) return <div className="flex justify-center py-20"><Loader2 size={28} className="animate-spin text-slate-300" /></div>;

  return (
    <div className="p-4 lg:p-8 max-w-5xl mx-auto" data-testid="employee-detail-page">
      <button className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4" onClick={() => navigate("/employes")} data-testid="back-to-employees">
        <ArrowLeft size={15} /> Employés
      </button>

      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-4">
            <div className="w-14 h-14 shrink-0 rounded-full bg-[#26788A] text-white flex items-center justify-center font-bold text-lg">
              {(employee.first_name?.[0] || "") + (employee.last_name?.[0] || "")}
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-slate-900 break-words [overflow-wrap:anywhere]" data-testid="employee-name">{employee.first_name} {employee.last_name}</h1>
              <div className="flex items-center gap-2 flex-wrap mt-1">
                <span className="text-sm text-slate-500">{employee.position || "Poste non renseigné"}</span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">{CONTRACT_LABELS[employee.contract_type] || employee.contract_type}</span>
                {employee.is_active === false && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-600">Inactif</span>}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="edit-employee-btn"><Pencil size={14} className="mr-1" /> Modifier</Button>
            <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setDeleteOpen(true)} data-testid="delete-employee-btn"><Trash2 size={14} /></Button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mt-4 pt-4 border-t border-slate-100 text-sm [&>div]:min-w-0 [&>div]:[overflow-wrap:anywhere] [&_svg]:shrink-0" data-testid="employee-details">
          <div className="flex items-center gap-2 text-slate-600"><Mail size={14} className="text-slate-400" /> {employee.email || "—"}</div>
          <div className="flex items-center gap-2 text-slate-600"><Phone size={14} className="text-slate-400" /> {employee.phone || "—"}</div>
          <div className="flex items-center gap-2 text-slate-600"><MapPin size={14} className="text-slate-400" /> {[employee.address, employee.postal_code, employee.city].filter(Boolean).join(", ") || "—"}</div>
          <div className="flex items-center gap-2 text-slate-600"><Euro size={14} className="text-slate-400" /> {employee.salary_gross_monthly ? `${employee.salary_gross_monthly.toLocaleString("fr-FR")} € brut/mois` : "—"}</div>
          <div className="flex items-center gap-2 text-slate-600"><ShieldCheck size={14} className="text-slate-400" /> {employee.social_security_number || "—"}</div>
          <div className="flex items-center gap-2 text-slate-600"><CreditCard size={14} className="text-slate-400" /> {employee.iban || "—"}</div>
          <div className="text-slate-600">Embauché le <span className="font-medium">{fmtDate(employee.hire_date)}</span></div>
          {employee.end_date && <div className="text-slate-600">Fin : <span className="font-medium">{fmtDate(employee.end_date)}</span></div>}
        </div>
        {employee.notes && <p className="text-xs text-slate-500 bg-slate-50 rounded-md px-3 py-2 mt-3">{employee.notes}</p>}
      </div>

      {/* Tabs */}
      <Tabs defaultValue={canViewPayslips ? "payslips" : "documents"}>
        <TabsList className="mb-3 grid grid-cols-2 sm:flex h-auto w-full sm:w-fit [&>button]:min-h-11 [&>button]:whitespace-normal [&>button]:text-xs sm:[&>button]:text-sm" data-testid="employee-detail-tabs">
          {canViewPayslips && <TabsTrigger value="payslips" data-testid="tab-payslips">Bulletins de salaire</TabsTrigger>}
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="contracts" data-testid="tab-contracts">Contrats</TabsTrigger>
          <TabsTrigger value="planning" data-testid="tab-planning">Planning</TabsTrigger>
        </TabsList>
        {canViewPayslips && <TabsContent value="payslips">
          <FileSection employeeId={id} category="payslip" withPeriod emptyLabel="Aucun bulletin de salaire. Sélectionnez le mois puis ajoutez le PDF." />
        </TabsContent>}
        <TabsContent value="documents">
          <FileSection employeeId={id} category="document" emptyLabel="Aucun document (pièce d'identité, RIB, diplômes...)." />
        </TabsContent>
        <TabsContent value="contracts">
          <FileSection employeeId={id} category="contract" emptyLabel="Aucun contrat. Ajoutez le contrat de travail signé et ses avenants." />
        </TabsContent>
        <TabsContent value="planning">
          <PlanningTab employeeId={id} />
        </TabsContent>
      </Tabs>

      <EmployeeFormDialog open={editOpen} onClose={() => setEditOpen(false)} employee={employee} onSaved={load} />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid="delete-employee-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet employé ?</AlertDialogTitle>
            <AlertDialogDescription>
              La fiche de <strong>{employee.first_name} {employee.last_name}</strong>, ses fichiers (bulletins, documents, contrats) et son planning seront définitivement supprimés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={handleDelete} data-testid="confirm-delete-employee">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
